import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getToken } from "../auth/token";
import AuthPageShell from "../components/AuthPageShell";

const RAW_API_BASE = process.env.REACT_APP_API_URL || "";
const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/, "");

function safeInternalPath(next) {
    if (typeof next !== "string" || next === "" || !next.startsWith("/") || next.startsWith("//")) {
        return null;
    }
    return next;
}

export default function Login() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, loginWithToken, ready } = useAuth();
    const nextAfterLogin = safeInternalPath(searchParams.get("next"));
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);

    const setFormMessage = (text, type = "error") => {
        setMessage(text);
        setMessageType(type);
    };

    if (!ready && getToken()) {
        return (
            <AuthPageShell>
                <p className="cms-page-loading">Загрузка...</p>
            </AuthPageShell>
        );
    }
    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    async function onSubmit(e) {
        e.preventDefault();
        setMessage("");
        setEmailVerificationRequired(false);
        try {
            const res = await fetch(`${API_BASE}/api/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: identifier, password }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                const backendMessage = String(data?.message || data?.error || "").trim();
                const isInvalidCredentials = backendMessage.toLowerCase() === "invalid credentials.";
                const isEmailNotVerified = backendMessage.toLowerCase().includes("email not verified");

                if (res.status >= 500) {
                    console.error("Login failed with server error", {
                        status: res.status,
                        response: data,
                    });
                    setFormMessage("Что-то пошло не так. Внутренняя ошибка сервера.");
                    return;
                }

                if (isEmailNotVerified) {
                    setEmailVerificationRequired(true);
                    setFormMessage("Подтвердите email перед входом. Проверьте письмо со ссылкой подтверждения.");
                    return;
                }

                setFormMessage(
                    isInvalidCredentials
                        ? "Неверный логин или пароль"
                        : (backendMessage || "Неверный Email или пароль")
                );
                return;
            }

            const token = data.token;
            if (!token) {
                setFormMessage("Ответ сервера без токена");
                return;
            }

            await loginWithToken(token, data.refresh_token || "");
            navigate(nextAfterLogin ?? "/", { replace: true });
        } catch (error) {
            console.error("Login request failed", error);
            setFormMessage("Ошибка сети");
        }
    }

    async function resendVerificationEmail() {
        const email = identifier.trim();
        if (!email) {
            setFormMessage("Укажите email, чтобы отправить письмо повторно.");
            return;
        }

        setResendingVerification(true);
        try {
            const res = await fetch(`${API_BASE}/api/verify/resend`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setFormMessage(data.error || data.message || "Не удалось отправить письмо подтверждения.");
                return;
            }
            setFormMessage(data.message || "Письмо подтверждения отправлено повторно.", "success");
        } catch (error) {
            console.error("Verification email resend failed", error);
            setFormMessage("Ошибка сети");
        } finally {
            setResendingVerification(false);
        }
    }

    return (
        <AuthPageShell>
            <h1 className="auth-page-title">Вход</h1>
            <form onSubmit={onSubmit} className="auth-form">
                <input
                    placeholder="Email"
                    type="text"
                    value={identifier}
                    onChange={(e) => {
                        setIdentifier(e.target.value);
                        setEmailVerificationRequired(false);
                    }}
                    className="ui-input"
                    required
                />
                <input
                    placeholder="Пароль"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="ui-input"
                    required
                />
                <button className="toolbar-btn" type="submit">
                    Войти
                </button>
            </form>
            <div className="auth-links">
                <Link to="/register" className="btn-outline">Регистрация</Link>
                <Link to="/forgot" className="btn-outline">Забыли пароль</Link>
            </div>
            {message && (
                <p className={messageType === "success" ? "form-message--success" : "form-message"}>
                    {message}
                </p>
            )}
            {emailVerificationRequired && (
                <button
                    type="button"
                    className="auth-resend-link"
                    onClick={resendVerificationEmail}
                    disabled={resendingVerification}
                >
                    {resendingVerification ? "Отправляем..." : "Отправить email повторно"}
                </button>
            )}
        </AuthPageShell>
    );
}
